import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  Eye,
  Activity,
  Gauge,
  AlertTriangle,
  AlertCircle,
  ArrowUpDown,
  Loader2,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { usePortfolioSummary, type PortfolioProject } from '@/hooks/usePortfolioSummary'
import { Skeleton } from '@/components/ui/skeleton'

type SortKey = 'name' | 'visitors' | 'uptime' | 'performance' | 'issues'
type SortDir = 'asc' | 'desc'

export function PortfolioDashboard() {
  const navigate = useNavigate()
  const { data, loading, error } = usePortfolioSummary()
  const [sortKey, setSortKey] = useState<SortKey>('visitors')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sortedProjects = useMemo(() => {
    if (!data?.projects) return []
    return [...data.projects].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1
      switch (sortKey) {
        case 'name':
          return dir * a.name.localeCompare(b.name)
        case 'visitors':
          return dir * (a.visitors - b.visitors)
        case 'uptime':
          return dir * ((a.uptimePercent ?? 0) - (b.uptimePercent ?? 0))
        case 'performance':
          return dir * ((a.performanceScore ?? 0) - (b.performanceScore ?? 0))
        case 'issues':
          return dir * (a.openIssues - b.openIssues)
        default:
          return 0
      }
    })
  }, [data?.projects, sortKey, sortDir])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
        Failed to load portfolio data: {error || 'Unknown error'}
      </div>
    )
  }

  const { totals, needsAttention } = data

  return (
    <div className="space-y-6">
      {/* Aggregate Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Eye}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
          label="Visitors (7d)"
          value={totals.visitors.toLocaleString()}
        />
        <StatCard
          icon={Users}
          iconBg="bg-purple-50"
          iconColor="text-purple-600"
          label="Pageviews (7d)"
          value={totals.pageviews.toLocaleString()}
        />
        <StatCard
          icon={Activity}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          label="Avg Uptime"
          value={totals.avgUptime !== null ? `${totals.avgUptime.toFixed(1)}%` : 'N/A'}
        />
        <StatCard
          icon={Gauge}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
          label="Avg Performance"
          value={totals.avgPerformance !== null ? `${totals.avgPerformance}` : 'N/A'}
        />
      </div>

      {/* Needs Attention */}
      {needsAttention.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-red-800 flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4" />
            Needs Attention ({needsAttention.length})
          </h3>
          <div className="space-y-2">
            {needsAttention.map((alert, i) => (
              <button
                key={i}
                onClick={() => navigate(`/project/${alert.projectId}`)}
                className="w-full flex items-center gap-3 p-2.5 bg-white border border-red-100 rounded-lg hover:bg-red-50 transition-colors text-left"
              >
                <AlertCircle className={cn(
                  'w-4 h-4 flex-shrink-0',
                  alert.severity === 'critical' ? 'text-red-600' : 'text-amber-500'
                )} />
                <span className="text-sm font-medium text-framed-neutral-800 flex-1 truncate">{alert.projectName}</span>
                <span className="text-xs text-framed-neutral-500">{alert.reason}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Project Table */}
      <div className="bg-framed-neutral-100 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-framed-neutral-300">
          <h2 className="text-base font-semibold text-framed-neutral-800">
            All Projects ({totals.projectCount})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-framed-neutral-300">
                <SortableHeader label="Project" sortKey="name" currentKey={sortKey} dir={sortDir} onSort={handleSort} className="pl-5" />
                <SortableHeader label="Visitors" sortKey="visitors" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortableHeader label="Uptime" sortKey="uptime" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortableHeader label="Performance" sortKey="performance" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortableHeader label="Open Issues" sortKey="issues" currentKey={sortKey} dir={sortDir} onSort={handleSort} className="pr-5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-framed-neutral-200">
              {sortedProjects.map(project => (
                <tr
                  key={project.id}
                  onClick={() => navigate(`/project/${project.id}`)}
                  className="hover:bg-framed-neutral-50 cursor-pointer transition-colors"
                >
                  <td className="pl-5 py-3 font-medium text-framed-neutral-800">{project.name}</td>
                  <td className="py-3 text-framed-neutral-600">{project.visitors.toLocaleString()}</td>
                  <td className="py-3">
                    <UptimeBadge value={project.uptimePercent} isUp={project.isUp} />
                  </td>
                  <td className="py-3">
                    <PerformanceBadge value={project.performanceScore} />
                  </td>
                  <td className="pr-5 py-3">
                    <span className={cn(
                      'text-framed-neutral-600',
                      project.openIssues >= 10 && 'text-red-600 font-semibold'
                    )}>
                      {project.openIssues}
                    </span>
                  </td>
                </tr>
              ))}
              {sortedProjects.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-framed-neutral-500">
                    No projects found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// -- Sub-components --

function StatCard({ icon: Icon, iconBg, iconColor, label, value }: {
  icon: React.ComponentType<{ className?: string }>
  iconBg: string
  iconColor: string
  label: string
  value: string
}) {
  return (
    <div className="bg-framed-neutral-100 rounded-2xl p-5">
      <div className="flex items-center gap-4">
        <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0', iconBg)}>
          <Icon className={cn('w-6 h-6', iconColor)} />
        </div>
        <div>
          <p className="text-[11px] font-medium tracking-wide uppercase text-framed-neutral-500">{label}</p>
          <p className="text-2xl font-bold text-framed-neutral-800">{value}</p>
        </div>
      </div>
    </div>
  )
}

function SortableHeader({ label, sortKey, currentKey, dir, onSort, className }: {
  label: string
  sortKey: SortKey
  currentKey: SortKey
  dir: SortDir
  onSort: (key: SortKey) => void
  className?: string
}) {
  const isActive = currentKey === sortKey
  return (
    <th className={cn('py-3 text-left font-medium text-framed-neutral-500 whitespace-nowrap', className)}>
      <button
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-framed-neutral-800 transition-colors"
      >
        {label}
        <ArrowUpDown className={cn('w-3 h-3', isActive ? 'text-framed-neutral-800' : 'text-framed-neutral-400')} />
      </button>
    </th>
  )
}

function UptimeBadge({ value, isUp }: { value: number | null; isUp: boolean | null }) {
  if (value === null) return <span className="text-framed-neutral-400">N/A</span>
  if (isUp === false) return <span className="text-red-600 font-semibold">DOWN</span>
  return (
    <span className={cn(
      value >= 99.9 ? 'text-emerald-600' : value >= 99 ? 'text-amber-600' : 'text-red-600'
    )}>
      {value.toFixed(1)}%
    </span>
  )
}

function PerformanceBadge({ value }: { value: number | null }) {
  if (value === null) return <span className="text-framed-neutral-400">N/A</span>
  return (
    <span className={cn(
      value >= 90 ? 'text-emerald-600' : value >= 50 ? 'text-amber-600' : 'text-red-600',
      'font-medium'
    )}>
      {value}
    </span>
  )
}
